import { CreditCard, House, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { ROUTES } from '@/shared/constants/routes';
import { cn } from '@/shared/lib/cn';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Only `/` needs exact matching; the others own their whole subtree. */
  end?: boolean;
}

// DOM order is visual order from the inline start — the RIGHT edge in RTL.
// The mockups put خانه on the right, so it comes first here.
const ITEMS: readonly NavItem[] = [
  { to: ROUTES.home, label: 'خانه', icon: House, end: true },
  { to: ROUTES.payments, label: 'پرداخت‌ها', icon: CreditCard },
  { to: ROUTES.account, label: 'حساب کاربری', icon: User },
];

/**
 * Primary navigation. Fixed to the bottom of the viewport on every main tab,
 * clearing the iOS home indicator via `pb-safe`.
 *
 * Labels are visually hidden but present for screen readers — the design uses
 * icons alone, and an icon-only control with no accessible name is unusable
 * without sight.
 */
export function BottomNav() {
  return (
    <nav aria-label="ناوبری اصلی" className="fixed inset-x-0 bottom-0 z-40">
      {/* The bar's chrome lives on an app-shell-wide inner element so it lines
          up exactly with the app column on desktop instead of spanning the
          whole viewport. */}
      <div className="app-shell bg-card/95 border-border pb-safe border-t backdrop-blur-md lg:border-x">
        <ul className="flex items-stretch justify-around">
          {ITEMS.map(({ to, label, icon: Icon, end }) => (
            <li key={to} className="flex-1">
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    'flex h-14 flex-col items-center justify-center gap-1 text-[11px] transition-colors',
                    isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {/* Colour and stroke weight carry the active state. Filling
                      the glyph turns outline icons into solid blocks — a
                      filled `CreditCard` is just a purple rectangle. */}
                    <Icon className="size-6" strokeWidth={isActive ? 2.4 : 1.8} aria-hidden />
                    <span className="sr-only">{label}</span>
                  </>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
